import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { ICommand } from '../../core/commands/ICommand';
import type { FieldValue } from '../../core/fields/types';
import { UpdateNodeFieldCommand } from '../../core/fields/commands/UpdateNodeFieldCommand';
import { BaseTool, type IToolComponentProps, type IToolContext, type IToolManifest } from '../../core/tools/interfaces/ITool';
import { ShapeNotesEditor } from '../../components/notes/ShapeNotesEditor';
import { ReadOnlyNotesRenderer } from '../../components/notes/ReadOnlyNotesRenderer';
import { serializeRichTextContent, projectRichTextToPlainText } from '../../lib/richText/notesProjection';
import { Pencil } from 'lucide-react';

type ShapeNotesParams = {
  notesRich?: unknown;
  nodeId?: string;
};

const ShapeNotesPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [shape] = context.getSelectedShapes();
  const selectedNode = (shape as any)?.data as { id: string; attrs?: Record<string, unknown> } | undefined;

  const [isEditing, setIsEditing] = useState(false);

  // Notes are rendered inline by ProcessPropertyPanel for flowchart diagrams
  if (context.diagramType === 'flowchart') return null;

  if (!selectedNode) {
    return null;
  }

  const attrs = (selectedNode.attrs ?? {}) as Record<string, unknown>;
  const value = typeof attrs.notesRich === 'string' ? attrs.notesRich : attrs.notes;

  // Check if notes have content
  const plainText = projectRichTextToPlainText(value);
  const hasContent = plainText.trim().length > 0;

  return (
    <div className="mt-3 border-t px-4 pt-3 pb-2" data-testid="tid.tools.shapeNotes.panel">
      <div className="flex items-center justify-between mb-2">
        <h3
          className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          data-testid="tid.tools.shapeNotes.title"
        >
          {t('shapeNotes.title', { defaultValue: 'Notes' })}
        </h3>
        {!isEditing && context.canWrite && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3 w-3" />
            {hasContent
              ? (t('shapeNotes.actions.edit', { defaultValue: 'Edit' }) as string)
              : (t('shapeNotes.actions.add', { defaultValue: 'Add' }) as string)}
          </button>
        )}
        {isEditing && (
          <button
            onClick={() => setIsEditing(false)}
            className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('shapeNotes.actions.done', { defaultValue: 'Done' }) as string}
          </button>
        )}
      </div>

      {isEditing ? (
        <ShapeNotesEditor
          nodeId={selectedNode.id}
          value={value}
          onChange={(nextValue) => onExecute({ notesRich: nextValue, nodeId: selectedNode.id })}
          disabled={!context.canWrite}
        />
      ) : hasContent ? (
        <div
          className="cursor-pointer hover:bg-muted/30 rounded-lg px-1 py-1 -mx-1 transition-colors"
          onClick={() => context.canWrite && setIsEditing(true)}
        >
          <ReadOnlyNotesRenderer nodeId={selectedNode.id} value={value} />
        </div>
      ) : (
        <div
          className="text-[13px] text-muted-foreground/50 cursor-pointer hover:text-muted-foreground py-1 transition-colors"
          onClick={() => context.canWrite && setIsEditing(true)}
        >
          Click to add notes...
        </div>
      )}
    </div>
  );
};

export class ShapeNotesTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'shape-notes',
    name: 'Shape Notes',
    icon: 'sticky-note',
    category: 'edit',
    description: 'Edit rich notes for the selected shape',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: false,
      statusBar: false,
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    // ProcessPropertyPanel handles notes inline for flowchart diagrams
    if (context.diagramType === 'flowchart') return false;
    if (context.selection.size !== 1) return false;
    const shapes = context.getSelectedShapes();
    if (shapes.length !== 1) return false;
    const node = (shapes[0] as any)?.data as { id?: string; type?: string; attrs?: Record<string, unknown> } | undefined;
    return Boolean(node?.id);
  }

  execute(params?: ShapeNotesParams): ICommand | null {
    if (!this.context || !params || !('notesRich' in params)) {
      return null;
    }

    const [shape] = this.context.getSelectedShapes();
    const node = (shape as any)?.data as { id?: string } | undefined;
    const nodeId = params.nodeId ?? node?.id;
    if (!nodeId) {
      return null;
    }

    const serializedNotesRich = serializeRichTextContent(params.notesRich);

    return new UpdateNodeFieldCommand(nodeId, 'notesRich', serializedNotesRich as FieldValue);
  }

  getPropertyPanelComponent(): React.FC<IToolComponentProps> {
    return ShapeNotesPanel;
  }
}
